// HTTP client for the platform's review-loop service.
//
// Endpoints surfaced to the UI (matched against services/review-loop/src/index.ts):
//   GET  /health
//   GET  /wip
//   GET  /metrics
//   GET  /runs?status=&jira_issue_key=&ticket_id=&limit=
//   GET  /runs/:id
//   GET  /runs/:id/events?follow=true&after_idx=N    (NDJSON stream)
//   POST /runs/:id/cancel
//   POST /trigger { ticket_path | jira_issue_key | pr_number }
//
// Base URL comes from VITE_API_BASE_URL (default: '' so the dev proxy in
// vite.config.ts handles routing during dev). On Fly/production, set it to
// the public review-loop URL (e.g. https://agentic-api.your-domain.com).
//
// Every request goes through `apiFetch`, which sets `credentials: 'include'`
// so the browser carries the Cloudflare Access JWT cookie on cross-origin
// requests when the deployment is behind CF Access. In dev (same-origin via
// the Vite proxy) `credentials: 'include'` is a no-op.

import type {
  ApiEventLine,
  ChatMessage,
  ChatPreferences,
  ChatPreferencesResponse,
  ChatStreamEvent,
  CodexOauthStatusResponse,
  ConfigDefaultsResponse,
  ConfigResponse,
  OpenRouterCreditsResponse,
  HealthResponse,
  RunDetailResponse,
  RunsResponse,
  StartRunResponse,
  StartRunTicket,
  WipResponse,
  RunStatus,
} from './types';

export const API_BASE_URL = (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? '';

export class ApiError extends Error {
  constructor(message: string, public readonly status: number, public readonly body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Shared fetch wrapper. The credentials: 'include' default is the whole
 * reason this helper exists: when the deployment is behind Cloudflare Access
 * and the ops-console runs at a different origin (console.<domain>) than the
 * API (api.<domain>), the browser must be told to send the CF_Authorization
 * cookie cross-origin. Without it, every request is opaque-failed as a
 * NetworkError as soon as Access tries to redirect to /cdn-cgi/access/login.
 *
 * Same-origin / dev-proxy mode treats it as a no-op. Callers can override by
 * passing their own `credentials` in init.
 */
async function apiFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  return fetch(input, {
    credentials: 'include',
    ...init,
  });
}

export type FetchErrorKind =
  | 'cors-or-auth'
  | 'origin-down'
  | 'server-error'
  | 'client-error'
  | 'aborted'
  | 'unknown';

/**
 * Best-effort classification of a fetch() failure or API error. Callers
 * surface different UX per branch — auth-link prompt vs. "server is down" vs.
 * "we got a 502" etc.
 *
 * Heuristic limits — read these before extending:
 *   - Browser-side `cors-or-auth` and `origin-down` are indistinguishable.
 *     Both surface as a TypeError with no Response. We default to
 *     `cors-or-auth` because that's the actionable case (operator can click a
 *     link to authenticate); `origin-down` only fires when the runtime gives
 *     us enough evidence (e.g. err.cause.code on Node fetch).
 *   - Response-driven branches (server-error / client-error) take precedence
 *     over error-driven ones: if the request reached the server, the status
 *     is the source of truth.
 *   - ApiError carries status; we use it the same way a Response would.
 */
export function classifyFetchError(err: unknown, response?: Response): FetchErrorKind {
  // Response (or ApiError carrying a status) is the source of truth: the
  // request reached the server. Any err in this branch came from response
  // processing, not the network. 2xx/3xx with an err falls into 'unknown'
  // (caller saw a response and tripped on something else — body parse, etc.)
  // rather than the network-failure buckets.
  if (response) {
    if (response.status >= 500) return 'server-error';
    if (response.status >= 400) return 'client-error';
    return 'unknown';
  }
  if (err instanceof ApiError) {
    if (err.status >= 500) return 'server-error';
    if (err.status >= 400) return 'client-error';
    return 'unknown';
  }

  // AbortError / TimeoutError can be DOMException (browsers, jsdom) or
  // plain Error. instanceof Error is unreliable across runtimes for
  // DOMException; check the .name property directly.
  if (err && typeof err === 'object') {
    const name = (err as { name?: unknown }).name;
    if (name === 'AbortError' || name === 'TimeoutError') return 'aborted';
  }

  // fetch() throws TypeError for network-level failures: CORS rejection,
  // opaque cross-origin redirects (e.g. CF Access /cdn-cgi/access/login),
  // DNS errors, connection refused, etc. Node's undici exposes a structured
  // cause.code for DNS/connection cases; browsers do not.
  if (err instanceof TypeError) {
    const cause = (err as { cause?: { code?: string } }).cause;
    if (cause && typeof cause.code === 'string') {
      const code = cause.code;
      if (
        code === 'ENOTFOUND' ||
        code === 'ECONNREFUSED' ||
        code === 'ECONNRESET' ||
        code === 'ETIMEDOUT'
      ) {
        return 'origin-down';
      }
    }
    return 'cors-or-auth';
  }

  return 'unknown';
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { /* ignore */ }
    const message = (body && typeof body === 'object' && 'error' in body)
      ? String((body as { error: unknown }).error)
      : `${res.status} ${res.statusText}`;
    throw new ApiError(message, res.status, body);
  }
  return res.json() as Promise<T>;
}

export async function getHealth(signal?: AbortSignal): Promise<HealthResponse> {
  const res = await apiFetch(`${API_BASE_URL}/health`, { signal });
  return jsonOrThrow<HealthResponse>(res);
}

export async function getWip(signal?: AbortSignal): Promise<WipResponse> {
  const res = await apiFetch(`${API_BASE_URL}/wip`, { signal });
  return jsonOrThrow<WipResponse>(res);
}

export interface ListRunsParams {
  status?: RunStatus | RunStatus[];
  jira_issue_key?: string;
  ticket_id?: string;
  limit?: number;
}

export async function listRuns(params: ListRunsParams = {}, signal?: AbortSignal): Promise<RunsResponse> {
  const q = new URLSearchParams();
  if (params.status) {
    q.set('status', Array.isArray(params.status) ? params.status.join(',') : params.status);
  }
  if (params.jira_issue_key) q.set('jira_issue_key', params.jira_issue_key);
  if (params.ticket_id) q.set('ticket_id', params.ticket_id);
  if (params.limit) q.set('limit', String(params.limit));
  const qs = q.toString();
  const res = await apiFetch(`${API_BASE_URL}/runs${qs ? `?${qs}` : ''}`, { signal });
  return jsonOrThrow<RunsResponse>(res);
}

export async function getRun(runId: string, signal?: AbortSignal): Promise<RunDetailResponse> {
  const res = await apiFetch(`${API_BASE_URL}/runs/${encodeURIComponent(runId)}`, { signal });
  return jsonOrThrow<RunDetailResponse>(res);
}

export async function cancelRun(runId: string): Promise<{ status: string; run_id?: string }> {
  const res = await apiFetch(`${API_BASE_URL}/runs/${encodeURIComponent(runId)}/cancel`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
  });
  return jsonOrThrow(res);
}

// M13: re-queue a failed/escalated/cancelled run with the prior session_id
// so the strategy enters its resume path. Server reconstructs the ticket
// from the source run's metadata snapshot. Returns the new run id +
// the source so the UI can chain them.
export interface ResumeRunResponse {
  run_id: string;
  queued: boolean;
  source_run_id: string;
  session_id: string;
}

export async function resumeRun(runId: string): Promise<ResumeRunResponse> {
  const res = await apiFetch(`${API_BASE_URL}/runs/${encodeURIComponent(runId)}/resume`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
  });
  return jsonOrThrow<ResumeRunResponse>(res);
}

// GET /runs/:id/attach — operator "teleport" into a pi-managed Fly Machine.
// Returns the flyctl one-liners to drop into the agent's live sandbox.
// 409 when the run isn't pi-managed; 410 when the Machine has been destroyed.
export interface AttachInfoResponse {
  info: {
    run_id: string;
    strategy: 'pi-managed';
    machine_id: string;
    fly_app: string;
    region: string | null;
    machine_state: string;
    commands: {
      ssh: string;
      logs: string;
      proxy: string;
    };
    shell_hints: string[];
    repo_path: string;
    branch: string | null;
    keepalive_until: string | null;
    /**
     * Fly token identifier — present when the server minted a scoped token
     * for this attach. Used by `revokeAttach()` to kill the token early.
     * Never the token value itself.
     */
    token_id?: string;
    token_ttl_seconds?: number;
    token_expires_at?: string;
    token_revoke_url?: string;
  };
}

export async function attachRun(runId: string, signal?: AbortSignal): Promise<AttachInfoResponse> {
  const res = await apiFetch(`${API_BASE_URL}/runs/${encodeURIComponent(runId)}/attach`, { signal });
  return jsonOrThrow<AttachInfoResponse>(res);
}

export interface RevokeAttachResponse {
  status: 'revoked' | 'no-op';
  token_id?: string;
  reason?: string;
}

/**
 * Kill a previously-issued attach token. Idempotent from the caller's POV —
 * already-expired or already-revoked tokens return 200. Use this when the
 * operator closes the attach modal so we don't leak a live shell token
 * for the full TTL.
 */
export async function revokeAttach(runId: string, tokenId: string, signal?: AbortSignal): Promise<RevokeAttachResponse> {
  const qs = new URLSearchParams({ token_id: tokenId }).toString();
  const res = await apiFetch(
    `${API_BASE_URL}/runs/${encodeURIComponent(runId)}/attach/revoke?${qs}`,
    { method: 'POST', signal },
  );
  return jsonOrThrow<RevokeAttachResponse>(res);
}

// POST /runs/:id/interject — queue a user message for the live pi-runner.
// Forwards to the Fly Machine's sidecar; lands at the next session.prompt()
// boundary (no abort). Server returns 409 when the strategy isn't pi-managed
// or no Machine is yet provisioned; 413 when the message exceeds the
// sidecar's 8 KiB cap.
export interface InterjectRunResponse {
  queued: true;
  message_id: string;
  run_id: string;
}

export async function interjectRun(runId: string, message: string): Promise<InterjectRunResponse> {
  const res = await apiFetch(`${API_BASE_URL}/runs/${encodeURIComponent(runId)}/interject`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ message }),
  });
  return jsonOrThrow<InterjectRunResponse>(res);
}

export interface TriggerBody {
  ticket_path?: string;
  jira_issue_key?: string;
  pr_number?: number;
}

export async function trigger(body: TriggerBody): Promise<unknown> {
  const res = await apiFetch(`${API_BASE_URL}/trigger`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    // source identifies ops-console as the run origin so the per-run
    // provenance artifact records 'web-ui' rather than 'http-api'.
    body: JSON.stringify({ ...body, source: 'web-ui' }),
  });
  return jsonOrThrow(res);
}

// POST /runs — inline ticket, fire-and-forget. The next /runs poll picks
// up the new card; the caller can subscribe to events independently.
//
// Retry policy: the proxy chain (Pages Function → CF Worker → Fly) has been
// observed to intermittently return a 2xx whose body is missing `run_id` —
// usually on the first request after a hard refresh, then it works on retry.
// Suspected causes: cold JWKS fetch in the Worker, CF Access session refresh
// mid-flight, or an upstream CORS-preflight cache miss. Rather than chase it
// every time, we retry once with a short backoff for the specific "2xx but
// no run_id" shape AND log the bad response so future debug has data. Real
// failures (4xx/5xx) propagate immediately via jsonOrThrow — no double work.
export async function startRun(ticket: StartRunTicket): Promise<StartRunResponse> {
  const doPost = async (): Promise<StartRunResponse> => {
    const res = await apiFetch(`${API_BASE_URL}/runs`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ticket, source: 'web-ui' }),
    });
    return jsonOrThrow<StartRunResponse>(res);
  };

  const first = await doPost();
  if (first && first.run_id) return first;

  console.warn(
    '[startRun] 2xx without run_id on first attempt; retrying after 500ms',
    first,
  );
  await new Promise((r) => setTimeout(r, 500));
  const second = await doPost();
  if (second && second.run_id) return second;

  // Two consecutive misses — surface as ApiError so the caller's catch
  // shows a clean error in the toast rather than silently appearing
  // "queued" with no actual run.
  throw new ApiError(
    'startRun returned 2xx without run_id (twice) — proxy chain is malformed',
    200,
    second,
  );
}

export interface StashRunResponse {
  run_id: string;
  queued: false;
  draft: true;
}

/**
 * POST /runs with `draft: true` — creates the runs row in 'draft' status and
 * skips the worker dispatch. The card lands in the Backlog column; the
 * operator promotes it later via `promoteRun()`.
 */
export async function stashRun(ticket: StartRunTicket): Promise<StashRunResponse> {
  const res = await apiFetch(`${API_BASE_URL}/runs`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ ticket, source: 'web-ui', draft: true }),
  });
  return jsonOrThrow<StashRunResponse>(res);
}

/**
 * POST /runs/:id/promote — transition a drafted run from 'draft' to 'queued'
 * and dispatch the run-ticket worker job. 404 when the run doesn't exist;
 * 409 when it's no longer in 'draft' (someone else already promoted or
 * cancelled it).
 */
export async function promoteRun(runId: string): Promise<{ run_id: string; queued: boolean }> {
  const res = await apiFetch(`${API_BASE_URL}/runs/${encodeURIComponent(runId)}/promote`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
  });
  return jsonOrThrow(res);
}

// Drain the full event history for a run via /runs/:id/events?follow=false.
// `/runs/:id` only returns `recent_events` capped at 10 by `idx ASC` (i.e.
// the oldest 10 — useless for a long run's chat tab), so the Drawer needs a
// way to load the complete timeline when it opens a terminal run. Returns
// events in idx-ASC order; the synthetic terminating frames the server
// emits (`run_complete`, `stream_closed`) are filtered out.
export async function fetchAllEvents(
  runId: string,
  signal?: AbortSignal,
): Promise<ApiEventLine[]> {
  const events: ApiEventLine[] = [];
  const opts: StreamRunEventsOptions = { follow: false };
  if (signal) opts.signal = signal;
  for await (const line of streamRunEvents(runId, opts)) {
    if (line.event === 'run_complete' || line.event === 'stream_closed') continue;
    events.push(line);
  }
  return events;
}

// GET /runs/:id/diff — unified PR diff as plain text. 404 means the run has
// no PR yet; surfaces as ApiError with status=404 so the caller can render
// the empty state.
export async function getDiff(runId: string, signal?: AbortSignal): Promise<string> {
  const res = await apiFetch(`${API_BASE_URL}/runs/${encodeURIComponent(runId)}/diff`, { signal });
  if (!res.ok) {
    let body: unknown;
    try { body = await res.json(); } catch { /* ignore */ }
    const message = (body && typeof body === 'object' && 'error' in body)
      ? String((body as { error: unknown }).error)
      : `${res.status} ${res.statusText}`;
    throw new ApiError(message, res.status, body);
  }
  return res.text();
}

// GET /config — fetched once per session by the useConfig hook. Never
// includes provider key values — only the set of provider names whose env
// vars are populated.
export async function getConfig(signal?: AbortSignal): Promise<ConfigResponse> {
  const res = await apiFetch(`${API_BASE_URL}/config`, { signal });
  return jsonOrThrow<ConfigResponse>(res);
}

// GET /providers/codex-oauth/status — preflight inspection of the host's
// ~/.codex/auth.json. Polled by SettingsModal on open. The endpoint never
// blocks page load — if it errors we surface as "Unknown" rather than
// failing the modal.
export async function getCodexOauthStatus(
  signal?: AbortSignal,
): Promise<CodexOauthStatusResponse> {
  const res = await apiFetch(`${API_BASE_URL}/providers/codex-oauth/status`, { signal });
  return jsonOrThrow<CodexOauthStatusResponse>(res);
}

// GET /providers/openrouter/credits — remaining-credit/usage badge for the
// header. Server caches for 5 min, so polling once on mount + every 5 min is
// fine. Returns state='unconfigured' when OPENROUTER_API_KEY isn't set; the
// pill self-hides in that case.
export async function getOpenRouterCredits(
  signal?: AbortSignal,
): Promise<OpenRouterCreditsResponse> {
  const res = await apiFetch(`${API_BASE_URL}/providers/openrouter/credits`, { signal });
  return jsonOrThrow<OpenRouterCreditsResponse>(res);
}

// M10: GET /config/defaults — full editor payload (baseline YAML, current
// overlay, merged effective config, available providers/models/strategies).
export async function getConfigDefaults(signal?: AbortSignal): Promise<ConfigDefaultsResponse> {
  const res = await apiFetch(`${API_BASE_URL}/config/defaults`, { signal });
  return jsonOrThrow<ConfigDefaultsResponse>(res);
}

// M10: PATCH /config/defaults — replace the runtime_config overlay with the
// supplied JSON object. Server validates the merged result against the YAML
// schema before persisting; a bad model id surfaces as ApiError(400).
export async function updateConfigDefaults(
  overlay: Record<string, unknown>,
  updatedBy?: string,
): Promise<ConfigDefaultsResponse> {
  const res = await apiFetch(`${API_BASE_URL}/config/defaults`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ overlay, ...(updatedBy ? { updated_by: updatedBy } : {}) }),
  });
  return jsonOrThrow<ConfigDefaultsResponse>(res);
}

// GET /chat/preferences — per-tenant chat orchestrator defaults. Returns
// stored values + the effective merge with built-in defaults so the modal
// always has a fully-populated form even before the operator has saved
// anything.
export async function getChatPreferences(signal?: AbortSignal): Promise<ChatPreferencesResponse> {
  const res = await apiFetch(`${API_BASE_URL}/chat/preferences`, { signal });
  return jsonOrThrow<ChatPreferencesResponse>(res);
}

// PATCH /chat/preferences — partial update. Pass `null` to clear a field,
// omit a field to leave it unchanged. Server validates provider/model
// against the live RouterConfig and refuses identical agent + reviewer
// providers; both surface as ApiError(400).
export async function updateChatPreferences(
  patch: ChatPreferences,
): Promise<ChatPreferencesResponse> {
  const res = await apiFetch(`${API_BASE_URL}/chat/preferences`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return jsonOrThrow<ChatPreferencesResponse>(res);
}

// M11: streamChat — POST /chat with the running message history, yields
// each NDJSON line as a parsed ChatStreamEvent. The reader mirrors
// streamRunEvents' buffer reassembly so partial chunks split mid-line are
// stitched back together before JSON.parse.
export async function* streamChat(
  messages: ChatMessage[],
  signal?: AbortSignal,
): AsyncGenerator<ChatStreamEvent, void, void> {
  const res = await apiFetch(`${API_BASE_URL}/chat`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ messages }),
    ...(signal ? { signal } : {}),
  });
  if (!res.ok || !res.body) {
    let bodyText = '';
    try { bodyText = await res.text(); } catch { /* ignore */ }
    throw new ApiError(
      `chat stream failed: ${res.status} ${bodyText.slice(0, 200)}`,
      res.status,
    );
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    let chunk: ReadableStreamReadResult<Uint8Array>;
    try {
      chunk = await reader.read();
    } catch {
      return;
    }
    if (chunk.done) break;
    buffer += decoder.decode(chunk.value, { stream: true });
    let nl: number;
    while ((nl = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, nl).trim();
      buffer = buffer.slice(nl + 1);
      if (!line) continue;
      try {
        yield JSON.parse(line) as ChatStreamEvent;
      } catch {
        // Skip malformed lines; the server may have written diagnostic
        // text outside the event protocol.
      }
    }
  }
  const tail = buffer.trim();
  if (tail) {
    try { yield JSON.parse(tail) as ChatStreamEvent; } catch { /* skip */ }
  }
}

// ---------------------------------------------------------------------------
// /runs/:id/events?follow=true — NDJSON stream consumer.
//
// The server writes one JSON object per line and never sends classic SSE
// frames. We read the body as a stream, split on \n, and yield parsed lines.
//
// Reconnect strategy: when the server-side `Last-Event-ID` support lands
// (the parallel agent is shipping it), pass the last seen `idx` as the
// query param `after_idx` and as the header `Last-Event-ID`. Both are
// accepted on the same path; the server picks whichever it understands.

export interface StreamRunEventsOptions {
  follow?: boolean;
  afterIdx?: number;
  signal?: AbortSignal;
}

export async function* streamRunEvents(
  runId: string,
  opts: StreamRunEventsOptions = {},
): AsyncGenerator<ApiEventLine, void, void> {
  const q = new URLSearchParams();
  if (opts.follow !== false) q.set('follow', 'true');
  if (typeof opts.afterIdx === 'number' && opts.afterIdx >= 0) {
    q.set('after_idx', String(opts.afterIdx));
  }
  const url = `${API_BASE_URL}/runs/${encodeURIComponent(runId)}/events?${q.toString()}`;

  const headers: Record<string, string> = {};
  if (typeof opts.afterIdx === 'number' && opts.afterIdx >= 0) {
    // For the upcoming server-side reconnect support — harmless when ignored.
    headers['Last-Event-ID'] = String(opts.afterIdx);
  }

  const res = await apiFetch(url, {
    headers,
    ...(opts.signal ? { signal: opts.signal } : {}),
  });
  if (!res.ok || !res.body) {
    let bodyText = '';
    try { bodyText = await res.text(); } catch { /* ignore */ }
    throw new ApiError(
      `event stream failed: ${res.status} ${bodyText.slice(0, 200)}`,
      res.status,
    );
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    let chunk: ReadableStreamReadResult<Uint8Array>;
    try {
      chunk = await reader.read();
    } catch {
      // Aborted or socket closed — yield nothing more.
      return;
    }
    if (chunk.done) break;
    buffer += decoder.decode(chunk.value, { stream: true });
    let nl: number;
    while ((nl = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, nl).trim();
      buffer = buffer.slice(nl + 1);
      if (!line) continue;
      try {
        const parsed = JSON.parse(line) as ApiEventLine;
        // The server emits `keepalive` lines every ~20s so the L7 proxy
        // (CF Worker / Pages Function) doesn't 524 the stream during long
        // tool calls. They're transport-layer only — consumers (chat
        // bubbles, run state) should never see them.
        if (parsed.event === 'keepalive') continue;
        yield parsed;
      } catch {
        // Bad line — skip; server may have written something non-JSON for
        // diagnostic reasons.
      }
    }
  }
  // Flush trailing buffer in case the last line didn't end with \n.
  const tail = buffer.trim();
  if (tail) {
    try {
      const parsed = JSON.parse(tail) as ApiEventLine;
      if (parsed.event !== 'keepalive') yield parsed;
    } catch { /* skip */ }
  }
}
